import React, { ForwardedRef, useCallback, useImperativeHandle, useMemo, useRef } from 'react';
import { useIntl } from 'umi';
import classNames from 'classnames';
import { orderBy } from 'lodash-es';
import Chart from '..';
import {
  ChartOption,
  ChartRef,
  IEChartsTooltipParams,
  PercentLineChartProps,
} from '../_config/_interface';
import TextBubbles from '@home/components/TextBubbles';
import { useBaseOptions } from '../chartConfigs/useBaseOptions';
import { setDataLabel, setXLabelAlign } from '../chartConfigs/plugs';
import { ITooltipRecord } from '../chartConfigs/types';
import { formatNumberByComma, getNullTextOfFilter } from '@/pages/datalab/utils/utils';
import { generateTooltipGrid } from '@customComp/Chart/chartConfigs/tooltip';
import {
  changeLegendLocation,
  getLegendConfig,
} from '@customComp/Chart/chartConfigs/legend/legendUtils';
import { LIGHT_COLOR_ARR } from '../_config/_constant';
import {
  buildLegendKey,
  buildLegendPaletteIndexMap,
  findRgbaForLegend,
} from '@/pages/datalab/utils/chartLegend';
import { setTooltipMaxSize } from '@customComp/Chart/chartConfigs/tooltip/config';
import useReferenceLine, {
  ReferenceLineSeriesName,
} from '@customComp/Chart/chartConfigs/useReferenceLine';
import { NULL_UI_DEFAULT } from '@dbl/core/chartEditor';
import { applyNullUiToPercentLineSeries } from '@customComp/Chart/chartConfigs/nullHandling';
import { setAloneSymbolOpacity, LINE_CHART_SYMBOL_EMPHASIS } from '@customComp/Chart/chartConfigs/seriesUtils';
import {
  buildAxisPercentLineSeries,
} from '../utils/axisPercent';
import type { IAxisPercentSeriesItem } from '../utils/axisPercent/types';
import { configurePercentLineYAxis } from '../utils/configurePercentLineYAxis';

const PercentLineChartInner: React.ForwardRefRenderFunction<
ChartRef,
PercentLineChartProps
> = (props, ref: ForwardedRef<ChartRef>) => {
  const { chartData, metrics, dimensions, chartConfig } = props;
  const { seriesData, xAxisData } = chartData || {};
  const { yAxisName, xLabel } = chartConfig || {};
  const isOnlyOneDimension = dimensions?.length === 1;
  const hasYAxisName = Boolean(yAxisName);
  const { formatMessage } = useIntl();
  const chartRef = useRef<ChartRef>(null);
  const chart = chartRef?.current?.cRef;
  const instance = (): ReturnType<ChartRef['instance']> =>
    chartRef?.current?.instance();

  useImperativeHandle(ref, () => ({
    instance,
  }));

  const legendData = useMemo(
    () => seriesData?.map(item => item?.name) ?? [],
    [seriesData],
  );

  const seriesColors = useMemo(() => {
    if (!seriesData?.length) {
      return LIGHT_COLOR_ARR;
    }
    const getLegendKey = (item: (typeof seriesData)[number]): string =>
      buildLegendKey({
        controlName: String(
          (item as { control_name_cn?: string; }).control_name_cn ?? '',
        ),
        seriesDataName: String(item?.name ?? item?.metric ?? ''),
      });
    const legendKeysInOrder: string[] = [];
    const legendKeySeen = new Set<string>();
    seriesData.forEach(item => {
      const legendKey = getLegendKey(item);
      if (!legendKey || legendKeySeen.has(legendKey)) {
        return;
      }
      legendKeySeen.add(legendKey);
      legendKeysInOrder.push(legendKey);
    });
    const legendKeyToPaletteIndex = buildLegendPaletteIndexMap(legendKeysInOrder);
    return seriesData.map((item, index) => {
      const legendKey = getLegendKey(item);
      const paletteIndex = legendKeyToPaletteIndex[legendKey] ?? index;
      return findRgbaForLegend(
        legendKey,
        paletteIndex,
        chartConfig?.legendColors,
      );
    });
  }, [chartConfig?.legendColors, seriesData]);

  const nullUi = chartConfig?.nullUi ?? NULL_UI_DEFAULT;

  const linePercentSeries = useMemo(() => {
    const baseSeries = buildAxisPercentLineSeries(seriesData, xAxisData).map(
      (seriesItem, index) => ({
        ...seriesItem,
        lineStyle: {
          type: 'solid',
          color: seriesColors[index],
        },
        itemStyle: {
          opacity: 0,
          color: seriesColors[index],
        },
        symbol: 'circle',
        emphasis: LINE_CHART_SYMBOL_EMPHASIS,
      }),
    );
    const withNullUi = applyNullUiToPercentLineSeries(baseSeries, nullUi);
    if (nullUi === 'ignore') {
      // ignore：跨 null 连线，默认隐藏圆点，确保折线本身可见（connectNulls 由 applyNullUi 写入）。
      // 不调用 setAloneSymbolOpacity：否则折线上部分圆点显示（原孤立点）、部分不显示，视觉不一致。
      // 不设置 showSymbol: false，与普通折线一致，hover 时由 emphasis 显示孤立点。
      return (withNullUi as IAxisPercentSeriesItem[]).map(seriesItem => ({
        ...seriesItem,
        lineStyle: {
          ...seriesItem.lineStyle,
          opacity: 1,
        },
      }));
    }
    setAloneSymbolOpacity(withNullUi);
    return withNullUi;
  }, [nullUi, seriesColors, seriesData, xAxisData]);

  const tooltipFormatterInner = useCallback(
    (params: IEChartsTooltipParams | IEChartsTooltipParams[]) => {
      const rawParams = Array.isArray(params) ? params : [params];
      // 参考线系列 data 为原始数值，无 dimensions/metricName，需从 axis tooltip 中排除
      const tooltipParams = rawParams.filter(item => {
        if (item?.seriesName === ReferenceLineSeriesName) {
          return false;
        }
        const { data } = item || {};
        return data !== null && data !== undefined && typeof data === 'object';
      });
      // 百分比折线图无 ToolTipSetting 配置项，Tooltip 始终展示 Total
      const showTotal = true;
      const sortParams = orderBy(
        tooltipParams,
        [(item: { data?: { oPercent?: number; }; }) => item?.data?.oPercent ?? -Infinity],
        ['desc'],
      )?.sort((a, b) => {
        if (a?.seriesName === 'Others') return 1;
        if (b?.seriesName === 'Others') return -1;
        return 0;
      });
      const axisHeader = rawParams[0]?.axisValueLabel;
      const columns = [
        {
          key: 'first',
          header: getNullTextOfFilter(axisHeader),
          align: 'start',
        },
      ];
      metrics?.forEach(name => {
        columns?.push({
          key: name,
          header: isOnlyOneDimension ? '' : name?.replace(/(t[0-2]_)/, '') || '',
          align: 'end',
        });
      });
      const records: ITooltipRecord[] = [];
      sortParams?.forEach(item => {
        const { data, marker } = item || {};
        const { metricName, oPercent, dimensions: dimText } = data || {};
        const name = metricName?.replace(/(t[0-2]_)/, '') || '';
        const col: ITooltipRecord = {
          first: isOnlyOneDimension
            ? `${marker}${name}`
            : `${marker}${dimText ?? ''}`,
        };
        const text = `${(Number(oPercent) || 0).toFixed(2)}%`;
        col[metricName] = `<span class="dbl-new-chart-tooltip-body-cell-value" >${text || '-'}</span>`;
        records?.push(col);
      });

      const totalCol: ITooltipRecord = {
        first: formatMessage({ id: 'dbl_chart_tooltip_total' }),
      };
      const totalValue = tooltipParams?.reduce((sum, item) => {
        const numValue = Number(item?.data?.oPercent) || 0;
        return Math.round((sum + numValue) * 1000) / 1000;
      }, 0);
      const totalText =
        Math.round(totalValue * 100) / 100 >= 100
          ? '100%'
          : `${totalValue?.toFixed(2)}%`;
      totalCol[metrics?.[0] || ''] = `<span class="dbl-new-chart-tooltip-body-cell-value" >${totalText}</span>`;
      const newRecords = showTotal ? [totalCol, ...(records || [])] : records;
      return generateTooltipGrid(columns, newRecords, {
        height: (chart?.current?.clientHeight || 379) + (showTotal ? 30 : 0),
        width: chart?.current?.clientWidth || 400,
      });
    },
    [chart, formatMessage, isOnlyOneDimension, metrics],
  );

  const baseOption = useBaseOptions({
    series: linePercentSeries,
    legendData,
    xAxisData,
    chartConfig,
    tooltipFormatter: tooltipFormatterInner,
    setOtherConfigFunc: (opt: ChartOption) => {
      let nextOption = configurePercentLineYAxis(opt);
      nextOption = { ...nextOption, color: seriesColors };
      nextOption = setDataLabel(nextOption, chartConfig, {
        label: {
          position: 'top',
        },
        metricFormatter: params => {
          const isPercent = params?.data?.isPercent;
          const metricValue = params?.data?.metricValue
            ? formatNumberByComma(params?.data?.metricValue)
            : '-';
          return isPercent ? `${metricValue}%` : metricValue;
        },
      });
      nextOption = setXLabelAlign(nextOption, { type: xLabel });
      changeLegendLocation({
        option: nextOption,
        position: chartConfig?.legendPosition,
      });
      const legendPositionPatch =
        nextOption.legend &&
        typeof nextOption.legend === 'object' &&
        !Array.isArray(nextOption.legend)
          ? nextOption.legend
          : {};
      nextOption = {
        ...nextOption,
        legend: getLegendConfig({
          data: legendData,
          ...legendPositionPatch,
        }),
      };
      nextOption = setTooltipMaxSize(nextOption, chartConfig, {
        chartLayoutRect: {
          width: chart?.current?.clientWidth || 400,
          height: chart?.current?.clientHeight || 379,
        },
      });
      return nextOption;
    },
  });

  const option = useReferenceLine({
    option: baseOption,
    referenceLines: chartConfig?.referenceLine,
    autoYRange: chartConfig?.yAxisRangeSwitch,
    isPercent: true,
  });

  return (
    <div className="analysis-chart-container">
      <div
        className={classNames('dbl-charts-top-yAxis-text', {
          'dbl-charts-top-yAxis-text--hidden': !hasYAxisName,
        })}
      >
        <div className="dbh-text-exceeds-bubble-left">
          <TextBubbles text={yAxisName} />
        </div>
      </div>
      <div className="analysis-chart-content">
        <Chart ref={chartRef} option={option} notMerge={true} />
      </div>
    </div>
  );
};

const PercentLineChart = React.forwardRef(PercentLineChartInner);

export default PercentLineChart;
